import { useSyncExternalStore } from "react";
import { queryClient } from "@/providers/LocalQueryClientProvider";
import { APP_CONFIG_QUERY_KEY, fetchAppConfig } from "@/queries/useAppConfigQuery";
import type { AppConfig } from "@/types/config";

/** The app config from the query cache, for non-React readers (Flare, plain utils). The query is the
 *  single copy — nothing mirrors it into a store. */
export function getCachedAppConfig(): AppConfig | undefined {
    return queryClient.getQueryData<AppConfig>(APP_CONFIG_QUERY_KEY);
}

/** GET /api/config through the shared query cache, outside React (e.g. the login submit handler):
 *  seeds the cache, so the dashboard's useAppConfigQuery mount reuses it instead of fetching again. */
export function fetchAppConfigIntoCache(): Promise<AppConfig> {
    return queryClient.fetchQuery({ queryKey: APP_CONFIG_QUERY_KEY, queryFn: () => fetchAppConfig() });
}

function subscribeToQueryCache(onChange: () => void): () => void {
    return queryClient.getQueryCache().subscribe(onChange);
}

/** Reactive read of the cached app config that needs no QueryClientProvider (e.g. IntlProvider, which
 *  sits above it) and never triggers a fetch. */
export function useCachedAppConfig(): AppConfig | undefined {
    return useSyncExternalStore(subscribeToQueryCache, getCachedAppConfig);
}
